import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

interface ImagePreviewModalProps {
  opened: boolean;
  src: string;
  onClose: () => void;
  alt?: string;
  /** Ảnh thay thế khi `src` lỗi. */
  fallbackSrc?: string;
}

const MIN_SCALE = 0.5;
const MAX_SCALE = 8;

/**
 * Xem ảnh toàn màn hình: phóng to / thu nhỏ / kéo (lăn chuột, chụm 2 ngón, nhấp đúp — `react-zoom-pan-pinch`)
 * và xoay 90°. Chỉ đổi cách HIỂN THỊ — không sửa, không lưu lại ảnh.
 *
 * - Render qua portal vào `body`: component hay được mở từ TRONG một modal khác (chi tiết đơn, chấm công...),
 *   modal cha có `transform` sẽ làm `position: fixed` bám theo modal cha thay vì cả màn hình.
 * - Phím tắt: ←/→ xoay, +/− phóng to/thu nhỏ, 0 đặt lại, Esc đóng. Esc bị chặn tại đây (capture) để không
 *   đóng luôn modal cha phía sau.
 * - Xoay 90°/270°: CSS không đổi khung của ảnh, nên đổi chéo giới hạn rộng/cao theo vùng xem — ảnh dọc xoay
 *   ngang vẫn nằm gọn, không bị cắt mép.
 */
const ImagePreviewModal: FC<ImagePreviewModalProps> = ({ opened, src, onClose, alt = "", fallbackSrc }) => {
  const [rotation, setRotation] = useState(0);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const viewportRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<ReactZoomPanPinchRef>(null);

  // Mỗi lần mở (hoặc đổi ảnh) bắt đầu lại từ 0°.
  useEffect(() => {
    if (opened) setRotation(0);
  }, [opened, src]);

  // Đo vùng xem để tính giới hạn kích thước ảnh khi xoay.
  useEffect(() => {
    if (!opened || !viewportRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setViewport({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(viewportRef.current);
    return () => observer.disconnect();
  }, [opened]);

  // Xoay thì đưa zoom/kéo về mặc định — giữ vị trí kéo cũ sau khi xoay dễ làm ảnh lệch khỏi khung.
  const rotate = useCallback((delta: number) => {
    setRotation((r) => r + delta);
    zoomRef.current?.resetTransform();
  }, []);

  const reset = useCallback(() => {
    setRotation(0);
    zoomRef.current?.resetTransform();
  }, []);

  useEffect(() => {
    if (!opened) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const actions: Record<string, () => void> = {
        Escape: onClose,
        ArrowLeft: () => rotate(-90),
        ArrowRight: () => rotate(90),
        "+": () => zoomRef.current?.zoomIn(),
        "=": () => zoomRef.current?.zoomIn(),
        "-": () => zoomRef.current?.zoomOut(),
        "0": reset,
      };
      const action = actions[e.key];
      if (!action) return;
      e.preventDefault();
      e.stopPropagation();
      action();
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [opened, onClose, rotate, reset]);

  if (!opened) return null;

  const isQuarterTurn = Math.abs(rotation / 90) % 2 === 1;
  const maxWidth = isQuarterTurn ? viewport.height : viewport.width;
  const maxHeight = isQuarterTurn ? viewport.width : viewport.height;

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex flex-col bg-black/90" role="dialog" aria-modal="true">
      {/* Thanh công cụ */}
      <div className="flex shrink-0 items-center justify-center gap-2 px-4 py-3">
        <ToolButton label="Xoay trái (←)" onClick={() => rotate(-90)}>
          <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
          <path d="M3 3v5h5" />
        </ToolButton>
        <ToolButton label="Xoay phải (→)" onClick={() => rotate(90)}>
          <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
          <path d="M21 3v5h-5" />
        </ToolButton>
        <span className="mx-1 h-6 w-px bg-white/20" />
        <ToolButton label="Thu nhỏ (−)" onClick={() => zoomRef.current?.zoomOut()}>
          <circle cx="11" cy="11" r="7" />
          <path d="M8 11h6M21 21l-5-5" />
        </ToolButton>
        <ToolButton label="Phóng to (+)" onClick={() => zoomRef.current?.zoomIn()}>
          <circle cx="11" cy="11" r="7" />
          <path d="M8 11h6M11 8v6M21 21l-5-5" />
        </ToolButton>
        <ToolButton label="Đặt lại (0)" onClick={reset}>
          <path d="M4 4v6h6" />
          <path d="M20 20v-6h-6" />
          <path d="M20 10A8 8 0 0 0 6.3 6.3L4 10M4 14a8 8 0 0 0 13.7 3.7L20 14" />
        </ToolButton>
        <span className="mx-1 h-6 w-px bg-white/20" />
        <ToolButton label="Đóng (Esc)" onClick={onClose}>
          <path d="M6 6l12 12M18 6L6 18" />
        </ToolButton>
      </div>

      {/* Vùng xem ảnh */}
      <div ref={viewportRef} className="relative min-h-0 flex-1 overflow-hidden">
        <TransformWrapper
          ref={zoomRef}
          minScale={MIN_SCALE}
          maxScale={MAX_SCALE}
          centerOnInit
          wheel={{ step: 0.2 }}
          doubleClick={{ mode: "toggle", step: 1.5 }}
        >
          <TransformComponent
            wrapperStyle={{ width: "100%", height: "100%" }}
            contentStyle={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <img
              src={src}
              alt={alt}
              draggable={false}
              onError={(e) => {
                if (fallbackSrc && e.currentTarget.src !== fallbackSrc) e.currentTarget.src = fallbackSrc;
              }}
              className="select-none object-contain transition-transform duration-200"
              style={{
                maxWidth: maxWidth || "100%",
                maxHeight: maxHeight || "100%",
                transform: `rotate(${rotation}deg)`,
              }}
            />
          </TransformComponent>
        </TransformWrapper>
      </div>
    </div>,
    document.body,
  );
};

const ToolButton: FC<{ label: string; onClick: () => void; children: ReactNode }> = ({ label, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/25"
  >
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      {children}
    </svg>
  </button>
);

export default ImagePreviewModal;
